// src/components/event/EventDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { getEvent, deleteEvent } from '../../services/api';
import DartboardCanvas from '../dartboard/DartboardCanvas';
import { auth } from '../../utils/firebaseClient';

const EventDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [error, setError] = useState('');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showDeletePasswordDialog, setShowDeletePasswordDialog] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);


  useEffect(() => {
    const loadEvent = async () => {
      try {
        if (!auth.currentUser) {
          navigate('/');
          return;
        }

        const eventData = await getEvent(id);
        setEvent(eventData);
        if (!eventData.password) {
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Error loading event:', error);
      }
    };
    loadEvent();

   /* Auto refresh removed temporarily
    // Auto refresh every 30 seconds
    const interval = setInterval(loadEvent, 30000);
    return () => clearInterval(interval);
    */
  }, [id, navigate]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (event?.password === password) {
      setIsAuthenticated(true);
      setError('');
      setPassword('');
    } else {
      setError('Incorrect password');
    }
  };

  const handleExportToExcel = () => {
    if (event.password) {
      setShowExportDialog(true);
    } else {
      exportData();
    }
  };

  const exportData = () => {
    if (!event.responses.length) {
      alert('No responses to export');
      return;
    }

    const data = event.responses.map((response, idx) => ({
      'User': Math.floor(idx / 4) + 1,
      'Question': event.questions[response.questionIndex],
      'Score': response.score,
      'Timestamp': new Date(response.timestamp).toLocaleString()
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Responses");
    XLSX.writeFile(wb, `${event.title}_responses.xlsx`);
    
    if (showExportDialog) {
      setShowExportDialog(false);
      setPassword('');
    }
  };

  const handleDeleteConfirm = () => {
    setShowDeleteConfirmation(false);
    if (event.password) {
      setShowDeletePasswordDialog(true);
    } else {
      deleteEventConfirmed();
    }
  };

  const deleteEventConfirmed = async () => {
    try {
      await deleteEvent(id, deletePassword);
      navigate('/my-events');
    } catch (error) {
      setError('Error deleting event');
    } finally {
      setShowDeletePasswordDialog(false);
      setDeletePassword('');
      setError('');
    }
  };

  if (!event) return <div className="loading">Loading...</div>;

  if (event.password && !isAuthenticated) {
    return (
      <div className="password-form">
        <h2>{event.title} - Results</h2>
        <form onSubmit={handlePasswordSubmit}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            className="input-field"
          />
          {error && <p className="error-message">{error}</p>}
          <button type="submit" className="submit-button">
            Access Results
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h2 className="event-title">{event.title} - Results</h2>
      </div>
      
      <DartboardCanvas
        questions={event.questions}
        responses={event.responses}
        readonly={true}
      />



      {showExportDialog && (
        <div className="modal-overlay">
          <div className="password-dialog">
            <h3>Export to Excel</h3>
            <p>Enter password to export data:</p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
            />
            {error && <p className="error-message">{error}</p>}
            <div className="dialog-buttons">
              <button onClick={exportData} className="confirm-button">
                Export
              </button>
              <button 
                onClick={() => {
                  setShowExportDialog(false);
                  setPassword('');
                  setError('');
                }} 
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirmation && (
        <div className="modal-overlay">
          <div className="confirmation-dialog">
            <h3>Delete Event</h3>
            <p>Are you sure you want to delete this event?</p>
            <div className="dialog-buttons">
              <button 
                onClick={handleDeleteConfirm}
                className="delete-button"
              >
                Yes, Delete
              </button>
              <button 
                onClick={() => setShowDeleteConfirmation(false)}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeletePasswordDialog && (
        <div className="modal-overlay">
          <div className="password-dialog">
            <h3>Delete Event</h3>
            <p>Enter password to confirm deletion:</p>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              className="input-field"
            />
            {error && <p className="error-message">{error}</p>}
            <div className="dialog-buttons">
              <button onClick={deleteEventConfirmed} className="delete-button">
                Delete
              </button>
              <button 
                onClick={() => {
                  setShowDeletePasswordDialog(false);
                  setDeletePassword('');
                  setError('');
                }} 
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDashboard;
